import type {
  NextStepTrigger,
  Placement,
  RequiredAction,
  TourDefinition,
  TourExitReason,
  TourStep,
} from '../../onboarding/models/tour.interfaces';
import { useOnboardingStore } from '../../onboarding/stores/useOnboardingStore';
import { APP_VERSION } from '../../../models/version';
import type { GuideStep, UiCommand } from '../services/adriApi';
import { useAdriStore } from '../stores/useAdriStore';
import { isSafeCommand } from './command-guard';

/** Firma mínima de `navigate` de React Router (inyectable en tests). */
export type Navigator = (to: string) => void;

export const GUIDE_EXIT_MESSAGE = 'Dejamos la guía aquí. Cuando quieras la retomamos.';
export const HIGHLIGHT_TARGET_MISSING_MESSAGE =
  'No encontré ese elemento en esta pantalla. Si quieres, te llevo a donde está.';
const DEFAULT_CLOSING = 'Listo, terminamos la guía.';

const REQUIRED_ACTIONS: RequiredAction[] = [
  'click',
  'click_optional',
  'fill',
  'fill_optional',
  'fill_and_save',
  'select',
  'select_optional',
  'select_or_create',
  'none',
];
const TRIGGERS: NextStepTrigger[] = [
  'onElementClick',
  'onFormSubmit',
  'onNextButton',
  'onTargetDisappear',
  'onFinish',
];
const PLACEMENTS: Placement[] = ['top', 'bottom', 'left', 'right'];

const pick = <T extends string>(value: unknown, allowed: T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

/** id plano ⇒ `[data-testid="id"]`; el selector del contrato pasa tal cual. */
export const toSelector = (target: string): string =>
  target.startsWith('[') ? target : `[data-testid="${target}"]`;

const currentPath = () => (typeof window !== 'undefined' ? window.location.pathname : '/');

const isUnder = (pathname: string, prefix: string) => {
  const base = prefix.split(/[?#]/)[0].replace(/\/+$/, '');
  return pathname === base || pathname.startsWith(base + '/');
};

const track = (eventType: string, metadata: Record<string, unknown>) => {
  // El backend restringe los eventos a una whitelist (US3): un 400 se tolera en silencio.
  try {
    useOnboardingStore.getState().trackEvent({ eventType, module: 'adri', metadata });
  } catch {
    /* silencioso */
  }
};

const checkRegistryVersion = (command: UiCommand) => {
  const known = useAdriStore.getState().status?.registryVersion;
  if (known && command.registryVersion && known !== command.registryVersion) {
    // eslint-disable-next-line no-console
    console.warn('ui_registry_mismatch', { known, received: command.registryVersion });
  }
};

export const toTourSteps = (steps: GuideStep[]): TourStep[] =>
  [...steps]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s, i) => ({
      id: s.id || `step-${i + 1}`,
      order: i + 1,
      target: toSelector(s.target),
      title: s.title,
      body: s.body,
      requiredAction: pick(s.requiredAction, REQUIRED_ACTIONS, 'none'),
      nextStepTrigger: pick(s.nextStepTrigger, TRIGGERS, 'onNextButton'),
      placement: pick(s.placement, PLACEMENTS, 'bottom'),
      skippable: s.skippable ?? true,
      ...(s.optional ? { optional: true } : {}),
    }));

/**
 * Sigue el paso activo del tour dinámico: `endDynamicTour` lo reinicia a 0 antes
 * de llamar `onExit`, así que se guarda para reportar el objetivo que faltó.
 */
const watchStep = (moduleId: string) => {
  let lastStep = 0;
  const unsubscribe = useOnboardingStore.subscribe((s) => {
    if (s.activeTourDefinition?.module === moduleId) lastStep = s.activeTourStep;
  });
  return { getStep: () => lastStep, unsubscribe };
};

const ensureRoute = (route: string | undefined, prefix: string | undefined, navigate: Navigator) => {
  const check = prefix || route;
  if (!check) return;
  if (!isUnder(currentPath(), check)) navigate(route || check);
};

const reopen = () => {
  const store = useAdriStore.getState();
  store.setCollapsed(false);
  store.open();
};

/** Ejecuta un comando `ui.guide` (contracts/ui-commands.md). */
export const runGuide = (command: UiCommand, navigate: Navigator): boolean => {
  if (command.type !== 'ui.guide' || !isSafeCommand(command)) return false;
  checkRegistryVersion(command);

  const guideId = command.guideId || command.requestId || 'guia';
  const moduleId = `adri:${guideId}`;
  const steps = toTourSteps(command.steps ?? []);
  if (steps.length === 0) return false;

  ensureRoute(command.route, command.routePrefix, navigate);
  useAdriStore.getState().setCollapsed(true);

  const watcher = watchStep(moduleId);
  const definition: TourDefinition = {
    module: moduleId,
    variant: 'adri',
    routePrefix: command.routePrefix,
    steps,
  };

  useOnboardingStore.getState().startDynamicTour(definition, {
    onFinish: () => {
      watcher.unsubscribe();
      reopen();
      const store = useAdriStore.getState();
      store.pushLocalMessage(command.closing?.text || DEFAULT_CLOSING, 'next-step');
      track('guide_completed', { guideId, milestone: command.milestone });
      void store.loadRoute();
    },
    onExit: (reason: TourExitReason) => {
      const stepIndex = watcher.getStep();
      watcher.unsubscribe();
      reopen();
      useAdriStore.getState().pushLocalMessage(GUIDE_EXIT_MESSAGE);
      track('guide_exited', { guideId, reason, step: stepIndex + 1 });
      if (reason === 'target-missing') {
        track('ui_target_missing', {
          guideId,
          screenId: command.screenId,
          target: steps[stepIndex]?.target ?? steps[0].target,
          appVersion: APP_VERSION,
        });
      }
    },
  });
  return true;
};

/** `ui.highlight` ⇒ guía informativa de un paso que expira a los `timeoutMs`. */
export const runHighlight = (command: UiCommand, navigate: Navigator): boolean => {
  if (command.type !== 'ui.highlight' || !isSafeCommand(command) || !command.target) return false;
  checkRegistryVersion(command);

  ensureRoute(command.route, command.route, navigate);
  useAdriStore.getState().setCollapsed(true);

  const moduleId = `adri:highlight:${command.requestId || Date.now()}`;
  const target = toSelector(command.target);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const done = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    reopen();
  };

  useOnboardingStore.getState().startDynamicTour(
    {
      module: moduleId,
      variant: 'adri',
      steps: [
        {
          id: 'highlight',
          order: 1,
          target,
          title: 'Aquí',
          body: command.message || 'Este es el lugar que buscas.',
          requiredAction: 'none',
          nextStepTrigger: 'onNextButton',
          placement: 'bottom',
          skippable: true,
        },
      ],
    },
    {
      onFinish: done,
      onExit: (reason) => {
        done();
        if (reason === 'target-missing') {
          useAdriStore.getState().pushLocalMessage(HIGHLIGHT_TARGET_MISSING_MESSAGE);
          track('ui_target_missing', {
            screenId: command.screenId,
            target,
            appVersion: APP_VERSION,
          });
        }
      },
    },
  );

  const timeoutMs = command.timeoutMs && command.timeoutMs > 0 ? command.timeoutMs : 30000;
  timer = setTimeout(() => {
    timer = null;
    const ob = useOnboardingStore.getState();
    if (ob.activeTourDefinition?.module === moduleId) ob.endDynamicTour('finish');
  }, timeoutMs);
  return true;
};

/** Ejecuta los comandos de interfaz recibidos del chat; descarta los inseguros. */
export const handleCommands = (commands: UiCommand[] | undefined | null, navigate: Navigator) => {
  for (const cmd of commands ?? []) {
    if (!isSafeCommand(cmd)) continue;
    if (cmd.type === 'ui.navigate' && cmd.route) navigate(cmd.route);
    else if (cmd.type === 'ui.highlight') runHighlight(cmd, navigate);
    else if (cmd.type === 'ui.guide') runGuide(cmd, navigate);
  }
};
